import React, { FC, useContext, useEffect, useState } from "react";
import { Avatar, Col, Drawer, Dropdown, Menu, Row } from "antd";
import { useIntl } from "react-intl";
import { ScreenSizeContext } from "../../pages/_app";
import {
  CalendarOutlined,
  CloseOutlined,
  HomeOutlined,
  LoginOutlined,
  LogoutOutlined,
  MenuOutlined,
  QuestionOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useRouter } from "next/router";
import {
  useFindCurrentPortalUserQuery,
  useLogoutMutation,
} from "../../src/generated/graphql";
import { client, dev } from "../../apollo/ApolloSetup";
import styles from "./page-wrapper.module.scss";
import { MenuMode } from "antd/es/menu";
import { AveliosButton } from "../common-layout/avelios-button";

interface AveliosNavigationItem {
  key: string;
  labelKey: string;
  icon?: React.ReactNode;
  onClick?: () => void;
}

export const defaultNavigation: AveliosNavigationItem[] = [
  {
    key: "home",
    labelKey: "home",
    icon: <HomeOutlined />,
  },
  {
    key: "helpcenter",
    labelKey: "helpcenter",
    icon: <QuestionOutlined />,
    onClick: () => window.open("https://avelios.helpkit.so", "_blank", "noopener,noreferrer"),
  },
  {
    key: "appointments",
    labelKey: "appointments",
    icon: <CalendarOutlined />,
  },
  // {
  //   key: "documents",
  //   label: formatMessage({ id: "documents" }),
  //   icon: <FileOutlined />,
  // },
];

export const Navigation: FC = () => {
  const { formatMessage } = useIntl();
  const { isMobile } = useContext(ScreenSizeContext);
  const router = useRouter();
  const route = router.route.replace("/", "");

  const [logout] = useLogoutMutation({
    onCompleted: () => {
      if (dev && typeof window !== "undefined") {
        localStorage.removeItem("token");
      }
      if (typeof window !== "undefined") {
        window.location.href = `${window.location.origin}/login`;
      }
      client.cache.reset();
    },
  });

  const { data } = useFindCurrentPortalUserQuery({
    fetchPolicy: "network-only",
  });

  const currentUser = data?.findCurrentPortalUser?.portalUser;

  const loggedIn = !!currentUser;

  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const openRoute = (route: string) => {
    setMenuOpen(false);
    router.push(route);
  };

  const userMenuItems: AveliosNavigationItem[] = [
    {
      key: "user",
      icon: <UserOutlined />,
      labelKey: "account",
      onClick: () => router.push("/user"),
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      labelKey: "action.logout",
      onClick: () => logout(),
    },
  ];

  const loginMenuItems: AveliosNavigationItem[] = [
    {
      key: "login",
      icon: <LoginOutlined />,
      labelKey: "action.login",
      onClick: () => router.push("/login"),
    },
  ];

  const userDropdown = (
    <Menu
      mode={"vertical"}
      className={styles.userDropdown}
      items={userMenuItems.map((item) => {
        const { icon, onClick, key } = item;
        return {
          icon,
          onClick,
          key,
          label: formatMessage({ id: item.labelKey }),
        };
      })}
    />
  );

  const [menuDirection, setMenuDirection] = useState<MenuMode>("horizontal");

  const [navigationMenuItems, setNavigationMenuItems] =
    useState<AveliosNavigationItem[]>(defaultNavigation);

  const [navClassName, setNavClassName] = useState(styles.navigation);

  useEffect(() => {
    if (isMobile) {
      setNavigationMenuItems([
        ...defaultNavigation,
        ...(loggedIn ? userMenuItems : loginMenuItems),
      ]);
      setMenuDirection("vertical");
    }

    const handleScroll = () => {
      if (window.scrollY > 90) {
        setNavClassName(styles.navigationBlur);
      } else {
        setNavClassName(styles.navigation);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [loggedIn]);

  const menu = (
    <Menu
      disabledOverflow={true}
      // @ts-ignore
      mode={menuDirection}
      selectedKeys={[route]}
      className={!isMobile ? navClassName : styles.navigation}
      items={navigationMenuItems.map((item) => {
        const { icon, key, onClick } = item;
        return {
          icon,
          key,
          onClick,
          label: formatMessage({ id: item!.labelKey }),
        };
      })}
      onSelect={(item) => {
        if (item.key === 'logout') {
          logout();
        } else if (item.key === 'helpcenter') {
          return;
        } else {
          openRoute(`/${item.key}`);
        }
      }}
    />
  );

  return (
    <>
      <Row className={styles.inlineNav}>
        {!loggedIn && (
          <Col>
            <AveliosButton
              onClick={() =>
                router.push({
                  pathname: "/login",
                  query: { destination: router.asPath },
                })
              }
              style={{ marginRight: 24 }}
            >
              {formatMessage({ id: "action.login" })}
            </AveliosButton>
          </Col>
        )}
        <Col>{menu}</Col>
        <Col>
          {loggedIn && (
            <Dropdown overlay={userDropdown} trigger={["click"]}>
              <Avatar
                size={30}
                icon={<UserOutlined />}
                className={styles.avatar}
              />
            </Dropdown>
          )}
        </Col>
      </Row>
      <MenuOutlined
        className={styles.drawerNav}
        onClick={() => setMenuOpen(true)}
      />
      <Drawer
        closable={false}
        headerStyle={{
          backgroundColor: "#1a518a",
          paddingLeft: 16,
        }}
        style={{ zIndex: 1001 }}
        width={"100%"}
        title={
          <div style={{ fontSize: "1.4em", color: "white", padding: 0 }}>
            <span>Avelios Medical</span>
            <span style={{ float: "right" }} onClick={() => setMenuOpen(false)}>
              <CloseOutlined />
            </span>
          </div>
        }
        bodyStyle={{ padding: 0 }}
        placement="right"
        onClose={() => setMenuOpen(false)}
        open={menuOpen}
      >
        <div style={{ backgroundColor: "#1a518a", height: "100%" }}>{menu}</div>
      </Drawer>
    </>
  );
};
